import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import utils from "../../universal/utils";

const styles = (theme) => ({
  root: {
    margin: theme.spacing(2),
    marginTop: 35,
  },
  paper: {
    padding: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  summaryBox: {
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  errorBox: {
    background: "#ffebee",
    border: "1px solid #f44336",
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  actions: {
    marginTop: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  "@media (min-width: 1024px)": {
    root: {
      width: 700,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 90,
    },
  },
});

const basePath = utils.getEdgeBasePath();

function formatAmount(amount) {
  return `₪${Number(amount || 0).toFixed(2)}`;
}

class PaymentLinkPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      submitting: false,
      link: null,
      error: "",
      payer: {
        name: "",
        email: "",
        phone: "",
      },
    };
  }

  componentDidMount() {
    this.loadLink();
  }

  loadLink = async () => {
    const { token } = this.props.match.params;

    this.setState({ loading: true, error: "" });

    try {
      const response = await fetch(`${basePath}/api/v1/payment-links/${token}`);
      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(this.getErrorMessage(data.reason || data.error));
      }

      this.setState({ link: data.link });
    } catch (error) {
      console.error("Error loading payment link:", error);
      this.setState({
        error: error.message || "לא ניתן לטעון את קישור התשלום",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  getErrorMessage = (reason) => {
    const messages = {
      NOT_FOUND: "קישור התשלום לא נמצא.",
      DISABLED: "קישור התשלום הזה אינו פעיל כרגע.",
      EXPIRED: "תוקף קישור התשלום פג.",
    };

    return messages[reason] || "לא ניתן לטעון את קישור התשלום.";
  };

  handleFieldChange = (field) => (event) => {
    const value = event.target.value;

    this.setState((prevState) => ({
      payer: {
        ...prevState.payer,
        [field]: value,
      },
    }));
  };

  handleSubmit = async () => {
    const { payer, link } = this.state;

    if (!payer.name.trim() || !payer.email.trim() || !payer.phone.trim()) {
      this.setState({ error: "נא למלא שם, אימייל וטלפון." });
      return;
    }

    this.setState({ submitting: true, error: "" });

    try {
      const response = await fetch(
        `${basePath}/api/v1/payment-links/${this.props.match.params.token}/initiate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ payer }),
        }
      );
      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error("לא ניתן היה להתחיל את התשלום.");
      }

      if (typeof window !== "undefined" && window.sessionStorage) {
        window.sessionStorage.setItem(
          "paymentLinkLastPath",
          `/pay/${this.props.match.params.token}`
        );
      }

      window.location.href = data.redirectUrl;
    } catch (error) {
      console.error("Error initiating payment:", error);
      this.setState({
        error: error.message || "לא ניתן היה להתחיל את התשלום.",
        submitting: false,
      });
    }
  };

  render() {
    const { classes } = this.props;
    const { loading, submitting, link, error, payer } = this.state;

    if (loading) {
      return (
        <div className={classes.root}>
          <Paper className={classes.paper} style={{ textAlign: "center" }}>
            <CircularProgress size={48} />
            <Typography variant="h6" style={{ marginTop: 16 }}>
              טוען קישור תשלום...
            </Typography>
          </Paper>
        </div>
      );
    }

    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <Typography variant="h4" className={classes.title}>
            {link?.title || "תשלום מיוחד"}
          </Typography>

          {error ? (
            <div className={classes.errorBox}>
              <Typography variant="body1">{error}</Typography>
            </div>
          ) : null}

          {link ? (
            <React.Fragment>
              <div className={classes.summaryBox}>
                <Typography variant="body1" gutterBottom>
                  <strong>תיאור:</strong> {link.description}
                </Typography>
                <Typography variant="h5">
                  <strong>סכום לתשלום:</strong> {formatAmount(link.amount)}
                </Typography>
              </div>

              <TextField
                label="שם מלא"
                value={payer.name}
                onChange={this.handleFieldChange("name")}
                fullWidth
                className={classes.formField}
              />
              <TextField
                label="אימייל"
                type="email"
                value={payer.email}
                onChange={this.handleFieldChange("email")}
                fullWidth
                className={classes.formField}
              />
              <TextField
                label="טלפון"
                value={payer.phone}
                onChange={this.handleFieldChange("phone")}
                fullWidth
                className={classes.formField}
              />

              <div className={classes.actions}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={submitting}
                  onClick={this.handleSubmit}
                >
                  {submitting ? "מעביר לתשלום..." : "לתשלום בכרטיס אשראי"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    window.location.href = "/";
                  }}
                >
                  חזרה לחנות
                </Button>
              </div>
            </React.Fragment>
          ) : null}
        </Paper>
      </div>
    );
  }
}

PaymentLinkPage.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default withStyles(styles)(PaymentLinkPage);
